import { ChevronDown } from 'lucide-react';
import { trackCTAClick } from '@/lib/analytics';
import { useTranslation } from 'react-i18next';
import { LanguageToggle } from '@/components/LanguageToggle';
import { useState, useEffect } from 'react';

const scrollToSection = (id) => {
  const el = document.getElementById(id);
  if (!el) return;
  const prefersReduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  el.scrollIntoView({ behavior: prefersReduced ? 'instant' : 'smooth', block: 'start' });
};

/* Split text into animatable units: words for Latin, characters for CJK */
const splitToUnits = (text) => {
  const hasCJK = /[\u4e00-\u9fff]/.test(text);
  if (hasCJK) {
    // Group CJK into 2-char chunks for natural rhythm
    const chars = text.split('');
    const chunks = [];
    for (let i = 0; i < chars.length; i += 2) {
      chunks.push(chars.slice(i, i + 2).join(''));
    }
    return { units: chunks, isCJK: true };
  }
  return { units: text.split(' '), isCJK: false };
};

/* Word-by-word reveal component */
const WordReveal = ({ text, active, baseDelay = 0, wordGap = 0.12, style = {} }) => {
  const { units, isCJK } = splitToUnits(text);
  return (
    <>
      {units.map((unit, i) => (
        <span
          key={i}
          style={{
            display: 'inline-block',
            opacity: active ? 1 : 0,
            filter: active ? 'blur(0px)' : 'blur(4px)',
            transform: active ? 'translateY(0)' : 'translateY(6px)',
            transition: `opacity 0.5s ease-out ${baseDelay + i * wordGap}s, filter 0.5s ease-out ${baseDelay + i * wordGap}s, transform 0.4s ease-out ${baseDelay + i * wordGap}s`,
            ...style,
          }}
        >
          {unit}{!isCJK && '\u00A0'}
        </span>
      ))}
    </>
  );
};

export const Hero = () => {
  const { t } = useTranslation();
  const [phase, setPhase] = useState(() => {
    try { return localStorage.getItem('verse_visited') ? 11 : 0; } catch { return 0; }
  });
  // Phase timeline:
  // 1 = LONGEVITY sharpens
  // 2 = , (comma) fades in
  // 3 = BY SCIENCE. fades in
  // 4 = AESTHETICS sharpens
  // 5 = , (comma) fades in
  // 6 = (pause) BY PRECISION. reveals
  // 7 = subtitle word-by-word
  // 8 = CTA button
  // 9 = scroll teaser
  // 10 = shimmer LONGEVITY
  // 11 = shimmer AESTHETICS

  useEffect(() => {
    const hasVisited = localStorage.getItem('verse_visited');
    if (hasVisited) {
      setPhase(11);
      return;
    }
    const timers = [
      setTimeout(() => setPhase(1), 500),
      setTimeout(() => setPhase(2), 1500),
      setTimeout(() => setPhase(3), 2200),
      setTimeout(() => setPhase(4), 3500),
      setTimeout(() => setPhase(5), 4500),
      setTimeout(() => setPhase(6), 6000),
      setTimeout(() => setPhase(7), 7500),
      setTimeout(() => setPhase(8), 10500),
      setTimeout(() => setPhase(9), 12000),
      setTimeout(() => setPhase(10), 13000),
      setTimeout(() => {
        setPhase(11);
        localStorage.setItem('verse_visited', '1');
      }, 13500),
    ];
    return () => timers.forEach(clearTimeout);
  }, []);

  const subtitle1 = t('hero.subtitle_1');
  const subtitle2 = t('hero.subtitle_2');
  const sub1Units = splitToUnits(subtitle1);
  const sub1Duration = sub1Units.units.length * 0.12;

  // Split ", BY SCIENCE." into comma + rest
  const h1b = t('hero.headline_1b');
  const h1b_comma = h1b.charAt(0);          // "," or "，"
  const h1b_rest = h1b.slice(1);            // " BY SCIENCE." or ""
  const h2b = t('hero.headline_2b');
  const h2b_comma = h2b.charAt(0);          // "," or "，"
  const h2b_rest = h2b.slice(1);            // " BY PRECISION." or ""

  // Detect CJK for tighter punctuation spacing
  const isCJK = /[\u4e00-\u9fff]/.test(t('hero.headline_1'));

  return (
    <section
      data-testid="hero-section"
      className="relative overflow-hidden min-h-[100dvh] flex flex-col"
      style={{ backgroundColor: 'var(--vc-bg)' }}
    >
      {/* Navigation */}
      <nav
        data-testid="top-nav"
        className="w-full py-5 px-6 sm:px-12 lg:px-16"
      >
        <div className="flex items-center justify-between">
          <img
            data-testid="brand-logo"
            src="/images/logo.png"
            alt="Verse Clinic"
            className="h-16 sm:h-18 lg:h-20"
            style={{ display: 'block' }}
          />
          <div className="flex items-center gap-3">
            <LanguageToggle />
            <button
              onClick={() => {
                trackCTAClick('nav_waitlist');
                scrollToSection('waitlist');
              }}
              className="hidden sm:inline-flex items-center h-10 px-6 text-sm font-medium rounded-xl border transition-colors duration-200 active:scale-[0.98]"
              style={{
                color: 'var(--vc-gold)',
                borderColor: 'var(--vc-gold)',
                backgroundColor: 'transparent',
              }}
              onMouseEnter={(e) => e.currentTarget.style.backgroundColor = 'rgba(214,168,74,0.12)'}
              onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
            >
              {t('hero.join_waitlist')}
            </button>
          </div>
        </div>
      </nav>

      {/* Hero Content — centered vertically */}
      <div className="flex-1 flex items-center justify-center px-6 sm:px-8">
        <div className="max-w-4xl text-center">
          {/* Headline */}
          <h1
            className="font-bold tracking-tight leading-[1.05]"
            style={{
              fontFamily: 'Inter, sans-serif',
              color: 'var(--vc-fg)',
              fontSize: 'clamp(36px, 5vw, 56px)',
            }}
          >
            {/* Line 1: LONGEVITY, BY SCIENCE. / 以科学定义长寿， */}
            <span style={{
              display: 'inline',
              filter: phase >= 1 ? 'blur(0px)' : 'blur(8px)',
              opacity: phase >= 1 ? 1 : 0.15,
              transform: phase >= 1 ? 'scale(1)' : 'scale(1.04)',
              transition: 'filter 1.2s ease-out, opacity 1.2s ease-out, transform 1.2s ease-out',
            }}>{t('hero.headline_1a')}<span className={phase >= 10 ? 'hero-gold-shimmer' : ''} style={{ color: 'var(--vc-gold)' }}>{t('hero.headline_1')}</span></span><span style={{
              display: 'inline',
              opacity: phase >= 2 ? 0.85 : 0.05,
              transition: 'opacity 0.6s ease-out',
              ...(isCJK ? { marginLeft: '-0.3em' } : {}),
            }}>{h1b_comma}</span>{h1b_rest && <span style={{
              display: 'inline',
              filter: phase >= 3 ? 'blur(0px)' : 'blur(6px)',
              opacity: phase >= 3 ? 0.85 : 0.05,
              transition: 'filter 1s ease-out, opacity 1s ease-out',
            }}>{h1b_rest}</span>}
            <br />
            {/* Line 2: AESTHETICS, BY PRECISION. / 以精准重塑年轻。 */}
            <span style={{
              display: 'inline',
              filter: phase >= 4 ? 'blur(0px)' : 'blur(8px)',
              opacity: phase >= 4 ? 1 : 0.15,
              transform: phase >= 4 ? 'scale(1)' : 'scale(1.04)',
              transition: 'filter 1.2s ease-out, opacity 1.2s ease-out, transform 1.2s ease-out',
            }}>{t('hero.headline_2a')}<span className={phase >= 11 ? 'hero-gold-shimmer' : ''} style={{ color: 'var(--vc-gold)' }}>{t('hero.headline_2')}</span></span><span style={{
              display: 'inline',
              opacity: phase >= 5 ? 0.85 : 0.05,
              transition: 'opacity 0.6s ease-out',
              ...(isCJK ? { marginLeft: '-0.3em' } : {}),
            }}>{h2b_comma}</span>{h2b_rest && <span style={{
              display: 'inline',
              filter: phase >= 6 ? 'blur(0px)' : 'blur(8px)',
              opacity: phase >= 6 ? 0.85 : 0.05,
              transition: 'filter 1.2s ease-out, opacity 1.2s ease-out',
            }}>{h2b_rest}</span>}
          </h1>

          {/* Subtitle — word by word */}
          <div className="mt-8 max-w-lg mx-auto">
            <p
              className="text-base sm:text-[17px] leading-relaxed"
              style={{ color: 'var(--vc-fg-muted)', fontFamily: 'Inter, sans-serif' }}
            >
              <WordReveal text={subtitle1} active={phase >= 7} baseDelay={0} wordGap={0.12} />
            </p>
            <p
              className="mt-1 text-base sm:text-[17px] leading-relaxed"
              style={{ color: 'var(--vc-fg-muted)', fontFamily: 'Inter, sans-serif' }}
            >
              <WordReveal text={subtitle2} active={phase >= 7} baseDelay={sub1Duration + 0.4} wordGap={0.12} />
            </p>
          </div>

          {/* CTA Buttons */}
          <div
            className="mt-10 flex flex-col sm:flex-row items-center justify-center gap-4"
            style={{
              opacity: phase >= 8 ? 1 : 0,
              transform: phase >= 8 ? 'translateY(0)' : 'translateY(10px)',
              transition: 'opacity 0.8s ease-out, transform 0.8s ease-out',
            }}
          >
            {/* Primary: DISCOVER NOW */}
            <button
              data-testid="hero-primary-cta-button"
              onClick={() => {
                trackCTAClick('hero_discover');
                const section = document.getElementById('aging-explorer');
                if (section) section.scrollIntoView({ behavior: 'smooth', block: 'start' });
                setTimeout(() => {
                  const revealBtn = document.querySelector('[data-testid="aging-reveal-button"]');
                  if (revealBtn) revealBtn.click();
                }, 800);
              }}
              className="hero-discover-btn inline-flex items-center justify-center h-12 w-52 text-sm font-semibold tracking-[0.08em] uppercase rounded-xl transition-all duration-300 active:scale-[0.97]"
              style={{
                backgroundColor: 'var(--vc-gold)',
                color: 'var(--vc-ink)',
                fontFamily: 'Inter, sans-serif',
                boxShadow: '0 0 20px rgba(214,168,74,0.25), 0 0 60px rgba(214,168,74,0.1)',
              }}
            >
              {t('hero.cta_discover')}
            </button>

            {/* Secondary: Join Waitlist */}
            <button
              data-testid="hero-waitlist-cta-button"
              onClick={() => {
                trackCTAClick('hero_waitlist');
                scrollToSection('waitlist');
              }}
              className="inline-flex items-center justify-center h-12 w-52 text-sm font-semibold tracking-[0.08em] uppercase rounded-xl border transition-colors duration-200 active:scale-[0.98]"
              style={{
                color: 'var(--vc-gold)',
                borderColor: 'rgba(214,168,74,0.35)',
                backgroundColor: 'transparent',
                fontFamily: 'Inter, sans-serif',
              }}
              onMouseEnter={(e) => e.currentTarget.style.backgroundColor = 'rgba(214,168,74,0.1)'}
              onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
            >
              {t('hero.join_waitlist')}
            </button>
          </div>
        </div>
      </div>

      {/* Scroll prompt — premium design */}
      <div
        data-testid="hero-scroll-prompt"
        className="pb-10 px-6 flex flex-col items-center cursor-pointer group"
        onClick={() => scrollToSection('insight')}
        style={{
          opacity: phase >= 9 ? 1 : 0,
          transform: phase >= 9 ? 'translateY(0)' : 'translateY(12px)',
          transition: 'opacity 1s ease-out, transform 1s ease-out',
        }}
      >
        {/* Gold gradient line */}
        <div
          className="w-px h-10 mb-4"
          style={{
            background: 'linear-gradient(to bottom, transparent, rgba(214,168,74,0.5))',
          }}
        />
        <span
          className="text-[11px] tracking-[0.2em] uppercase text-center mb-3"
          style={{
            color: 'rgba(214,168,74,0.6)',
            fontFamily: 'Inter, sans-serif',
            letterSpacing: '0.2em',
          }}
        >
          {t('hero.scroll_teaser')}
        </span>
        {/* Arrow with glow */}
        <div className="hero-bounce-arrow relative">
          <div
            className="absolute inset-0 rounded-full"
            style={{
              width: '32px',
              height: '32px',
              top: '-6px',
              left: '-6px',
              background: 'radial-gradient(circle, rgba(214,168,74,0.15) 0%, transparent 70%)',
            }}
          />
          <ChevronDown className="w-5 h-5 relative" style={{ color: 'var(--vc-gold)', opacity: 0.8 }} />
        </div>
      </div>
    </section>
  );
};
